import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../MainURL';
import SundaySchoolLayout from '../SundaySchoolLayout';
import { getSelectedDepartmentId, getSundaySchoolSession } from '../sundaySchoolSession';

type DashboardData = {
  classId: number;
  attendanceToday: { checked: number; present: number; total: number };
  weekly: { date: string; rate: number }[];
  longAbsent: string[];
  birthdaysToday: string[];
  unconfirmedNoticeCount: number;
  totalStudents?: number;
  pendingStaffCount?: number;
};

type UnconfirmedFamily = {
  recipientId: number;
  studentName: string;
  noticeTitle: string;
  daysSince: number;
  resentAt: string | null;
};

export default function SundaySchoolDashboard() {
  const session = getSundaySchoolSession();
  const [data, setData] = useState<DashboardData | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<UnconfirmedFamily[]>([]);

  useEffect(() => {
    if (!session) return;
    const params = { authChurch: session.user.authChurch, departmentId: getSelectedDepartmentId(), userId: session.user.id };
    axios
      .get(`${MainURL}/sundayschool/dashboard`, { params })
      .then(({ data }) => {
        if (data.ok) setData(data.data);
      })
      .catch((err) => console.error('GET /sundayschool/dashboard', err));
    axios
      .get(`${MainURL}/sundayschool/notices/unconfirmed`, { params })
      .then(({ data }) => {
        if (data.ok) setUnconfirmed(data.data.slice(0, 3));
      })
      .catch((err) => console.error('GET /sundayschool/notices/unconfirmed', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rate =
    data && data.attendanceToday.total > 0
      ? Math.round((data.attendanceToday.present / data.attendanceToday.total) * 100)
      : 0;

  return (
    <SundaySchoolLayout footNote="관리자 콘솔 · 전체 운영 현황">
      <div className="admin-dashboard">
        <div className="page-head">
          <div>
            <h1>
              {session?.church.name ?? '주일학교'} 🛠️{' '}
              <span className="badge badge-gray">교회 아이디: {session?.user.authChurch}</span>
            </h1>
            <div className="sub">관리자: {session?.user.name}</div>
          </div>
          <Link className="btn btn-primary" to="/manage-ss/attendance">
            ✅ 오늘 출석 체크하러 가기
          </Link>
        </div>

        {data && data.attendanceToday.checked < data.attendanceToday.total && (
          <div className="banner coral">
            <span className="b-emoji">⏰</span>
            <div className="grow">
              <strong>
                출석이 아직 남았어요 ({data.attendanceToday.checked}/{data.attendanceToday.total} 완료)
              </strong>
              <div className="mt8 banner-note">
                체크를 저장하면 학부모에게 "○○이가 도착했어요" 푸시가 전송됩니다.
              </div>
            </div>
            <Link className="btn banner-cta-white" to="/manage-ss/attendance">
              체크하기
            </Link>
          </div>
        )}

        <div className="stat-grid">
          <div className="stat">
            <div className="stat-ico bg-coral">✅</div>
            <div>
              <div className="stat-num">{rate}%</div>
              <div className="stat-label">
                오늘 출석률 ({data?.attendanceToday.present ?? 0}/{data?.attendanceToday.total ?? 0})
              </div>
            </div>
          </div>
          <div className="stat">
            <div className="stat-ico bg-sage">👥</div>
            <div>
              <div className="stat-num">{data?.totalStudents ?? 0}명</div>
              <div className="stat-label">부서 등록 학생</div>
            </div>
          </div>
          <div className="stat">
            <div className="stat-ico bg-sky">📣</div>
            <div>
              <div className="stat-num">{data?.unconfirmedNoticeCount ?? 0}건</div>
              <div className="stat-label">확인 안 한 공지</div>
            </div>
          </div>
          <div className="stat">
            <div className="stat-ico bg-yellow">🧑‍🏫</div>
            <div>
              <div className="stat-num">{data?.pendingStaffCount ?? 0}명</div>
              <div className="stat-label">승인 대기 교사</div>
            </div>
          </div>
        </div>

        <div className="grid2 mt24">
          <div className="card card-pad">
            <div className="card-title">
              📈 최근 4주 출석률{' '}
              <Link className="more" to="/manage-ss/attendance">
                자세히 →
              </Link>
            </div>
            <div className="chart">
              {(data?.weekly ?? []).map((w, i, arr) => (
                <div key={w.date} className="chart-col">
                  <div className="muted chart-rate">{w.rate}%</div>
                  <div className="chart-bar" style={{ height: Math.max(8, w.rate) }} />
                  <div className="muted mt8 chart-date">{i === arr.length - 1 ? '오늘' : w.date.slice(5)}</div>
                </div>
              ))}
            </div>
            <div className="muted mt16 chart-footnote">
              💡 장기결석 3주 이상: {(data?.longAbsent ?? []).join(', ') || '없음'}
            </div>
          </div>

          <div className="card card-pad">
            <div className="card-title">
              📌 관리자 할 일{' '}
              <span className="chip">
                {(data?.pendingStaffCount ?? 0) + (data?.unconfirmedNoticeCount ? 1 : 0)}
              </span>
            </div>
            <div className="row-list">
              {data && data.attendanceToday.checked < data.attendanceToday.total && (
                <div className="row">
                  <div className="avatar avatar-sm av-1">✔️</div>
                  <div className="grow">
                    <div className="t">출석 체크 마무리</div>
                    <div className="s">미체크 {data.attendanceToday.total - data.attendanceToday.checked}명</div>
                  </div>
                  <Link className="btn btn-outline btn-sm" to="/manage-ss/attendance">
                    가기
                  </Link>
                </div>
              )}
              {(data?.pendingStaffCount ?? 0) > 0 && (
                <div className="row">
                  <div className="avatar avatar-sm av-2">🧑‍🏫</div>
                  <div className="grow">
                    <div className="t">신규 교사 승인 대기</div>
                    <div className="s">{data?.pendingStaffCount}명 대기 중</div>
                  </div>
                  <Link className="btn btn-outline btn-sm" to="/manage-ss/staff">
                    가기
                  </Link>
                </div>
              )}
              <div className="row">
                <div className="avatar avatar-sm av-4">📣</div>
                <div className="grow">
                  <div className="t">알림장 확인 · 발송</div>
                  <div className="s">미확인 {data?.unconfirmedNoticeCount ?? 0}건</div>
                </div>
                <Link className="btn btn-outline btn-sm" to="/manage-ss/notices">
                  가기
                </Link>
              </div>
            </div>
          </div>

          <div className="card card-pad">
            <div className="card-title">
              👥 교적 · 계정 <span className="more">전체보기 →</span>
            </div>
            <div className="row-list">
              <div className="row">
                <div className="avatar av-1">학</div>
                <div className="grow">
                  <div className="t">전체 학생 {data?.totalStudents ?? 0}명</div>
                </div>
                <Link className="btn btn-outline btn-sm" to="/manage-ss/members">
                  관리
                </Link>
              </div>
              <div className="row">
                <div className="avatar av-2">교</div>
                <div className="grow">
                  <div className="t">승인 대기 {data?.pendingStaffCount ?? 0}명</div>
                </div>
                <Link className="btn btn-outline btn-sm" to="/manage-ss/staff">
                  관리
                </Link>
              </div>
              <div className="row">
                <div className="avatar av-3">반</div>
                <div className="grow">
                  <div className="t">교회 · 반 설정</div>
                </div>
                <Link className="btn btn-outline btn-sm" to="/manage-ss/church">
                  관리
                </Link>
              </div>
            </div>
          </div>

          <div className="card card-pad">
            <div className="card-title">
              📣 확인 안 한 학부모{' '}
              <Link className="more" to="/manage-ss/notices">
                재발송 →
              </Link>
            </div>
            <div className="row-list">
              {unconfirmed.length === 0 && <div className="muted">모두 확인했어요 🎉</div>}
              {unconfirmed.map((u) => (
                <div className="row" key={u.recipientId}>
                  <div className="avatar av-2">{u.studentName.slice(0, 1)}</div>
                  <div className="grow">
                    <div className="t">{u.studentName} 가족</div>
                    <div className="s">
                      {u.noticeTitle} · {u.daysSince}일째 미확인
                    </div>
                  </div>
                  <span className={`badge ${u.resentAt ? 'badge-warn' : 'badge-danger'}`}>
                    {u.resentAt ? '재발송됨' : '미발송'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SundaySchoolLayout>
  );
}
